import { DateValue } from '@internationalized/date';
import { RangeValue } from '@react-types/shared';
import { useCallback, useMemo } from 'react';
import { connect, ConnectedProps } from 'react-redux';

import { MessageType } from 'dashboard/lib/interfaces/Message';
import { TransactionType } from 'dashboard/models/transaction-type';
import { showMessage } from 'dashboard/redux/actions/messages';
import {
  clearMetrics,
  MetricsFilterParams,
  resetMetricsDateFilter,
  setMetricsFilters,
} from 'dashboard/redux/actions/metrics-transaction';
import { useListTransactionTypesQuery } from 'dashboard/redux/api/transaction-type';
import { getAdjustedFilters } from 'dashboard/redux/selectors/metrics-transaction';
import { RootState } from 'dashboard/redux/store';
import { loadMetricsTransactionVolumeOverTime } from 'dashboard/redux/thunk-actions/metrics-transaction';
import DashboardTransactionAnalyticsView from 'dashboard/views/DashboardTransactionAnalyticsView';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useEffectOnEnvironmentChange from 'lib/hooks/useOnEnvironmentChange';
import useRTKErrorHandler from 'lib/hooks/useRTKErrorHandler';
import useTable from 'lib/hooks/useTable';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';
import { translateDateValuetoLuxon } from 'lib/utils/filters';

const findSelectedTransactionTypes = (
  filters: MetricsFilterParams,
  transactionTypes: TransactionType[],
): TransactionType[] => {
  if (filters.transactionTypeIds == null) {
    return [];
  }
  return transactionTypes.filter((t) => filters.transactionTypeIds.includes(t.id));
};

const DashboardTransactionAnalyticsRoute = (props: PropsFromRedux) => {
  const { currentEnvironment, filters, resetMetricsDateFilter } = props;

  useEffectOnEnvironmentChange(() => {
    props.clearMetrics();
  });

  const { handleDateUpdate } = useTable({
    filters,
    setFilters: props.setMetricsFilters,
  });

  const handleTransactionTypeFilterUpdate = useCallback((transactionTypeIds: string[]) => {
    props.setMetricsFilters({
      transactionTypeIds: transactionTypeIds,
    });
  }, []);

  const {
    isLoading: isLoadingTransactionTypesList,
    data: { data: transactionTypes = [] } = {},
    error: transactionTypesError,
  } = useListTransactionTypesQuery({});

  useRTKErrorHandler({
    error: transactionTypesError,
    onError: () => ({ message: 'Failed to load Transaction Types.', level: MessageType.Failure }),
  });

  const selectedTransactionTypes = useMemo(
    () => findSelectedTransactionTypes(filters, transactionTypes),
    [filters, transactionTypes],
  );

  const {
    loading: isLoading,
    callback: loadTransactionMetrics,
    error: errorMetrics,
  } = useApiCallback(async () => {
    const overrideFilters = {
      ...props.filters,
      transactionTypeIds: selectedTransactionTypes?.map((txnType) => txnType.id) || [],
    };
    await props.loadMetricsTransactionVolumeOverTime(currentEnvironment, overrideFilters);
  }, [currentEnvironment, filters]);

  useApiErrorHandler({
    error: errorMetrics,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load transaction metrics.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage('We are experiencing network issues. Please try again.'),
  });

  const handleDateUpdateOrClearV2 = useCallback(
    (newDateRange: RangeValue<DateValue> | null) => {
      if (newDateRange) {
        const { startDate, endDate } = translateDateValuetoLuxon(newDateRange);
        startDate || endDate ? handleDateUpdate({ startDate, endDate }) : resetMetricsDateFilter();
      }
    },
    [filters],
  );

  return (
    <DashboardTransactionAnalyticsView
      filters={filters}
      handleDateUpdateV2={handleDateUpdateOrClearV2}
      handleTransactionTypeFilterUpdate={handleTransactionTypeFilterUpdate}
      isLoading={isLoading}
      loadTransactionMetrics={loadTransactionMetrics}
      isLoadingTransactionTypesList={isLoadingTransactionTypesList}
      volumeOverTime={props.volumeOverTime}
      queriedAt={props.queriedAt}
      transactionTypes={transactionTypes}
      selectedTransactionTypes={selectedTransactionTypes}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    currentEnvironment: state.currentEnvironment.currentEnvironment,
    volumeOverTime: state.metricsTransaction.volumeOverTime,
    queriedAt: state.metricsTransaction.queriedAt.volumeOverTime,
    filters: getAdjustedFilters(state),
  };
};

const mapDispatchToProps = {
  clearMetrics,
  setMetricsFilters,
  resetMetricsDateFilter,
  showMessage,
  loadMetricsTransactionVolumeOverTime,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardTransactionAnalyticsRoute);
